"use client";

import { Button } from "@/components/ui/button";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList, ComboboxTrigger } from "@/components/ui/combobox";
import { useContactFill } from "@/modules/contacts/ui/picker/contact-picker-select";
import { type ReactNode, useRef, type RefObject, useEffect, useState } from "react";

type FilterSelectProps = { name: string; options: FilterOption[]; defaultValue?: string; id?: string; searchLabel: string; emptyLabel: string; className?: string; onChange?: (value: string) => void };

/** A drop-down list with a search box: a button showing the chosen option opens a list that narrows as you type. The choice is sent with its form under `name` (like a normal select), so it works in any form. Give it `options` (value and the words shown), the starting `defaultValue`,
 *  and the words for the search box and for "nothing found". */
export function FilterSelect({ name, options, defaultValue, id, searchLabel, emptyLabel, className, onChange }: FilterSelectProps) {
  const { value, pick, clear } = usePickedValue(defaultValue, options[0]?.value ?? "");
  const hidden = useContactFill(name, (v) => { if (options.some((o) => o.value === v)) pick(v); });
  useFormReset(hidden, clear);
  useAnnounceChange(hidden, value);
  const selected = options.find((o) => o.value === value) ?? options[0];
  return (
    <>
      <input ref={hidden} type="hidden" name={name} value={value} />
      <Combobox items={options} value={selected} itemToStringLabel={(o: FilterOption) => o.label} isItemEqualToValue={(a: FilterOption, b: FilterOption) => a.value === b.value} onValueChange={(o: FilterOption | null) => { if (o) { pick(o.value); onChange?.(o.value); } }}>
        <ComboboxTrigger render={<Button id={id} type="button" variant="outline" className={`h-9 w-full justify-between border-input bg-transparent px-2.5 text-sm font-normal shadow-xs hover:bg-transparent aria-expanded:bg-transparent dark:bg-input/30 dark:hover:bg-input/50 ${className ?? ""}`} />}><span className="flex items-center gap-2 truncate">{selected?.icon}{selected?.label}</span></ComboboxTrigger>
        <FilterList searchLabel={searchLabel} emptyLabel={emptyLabel} />
      </Combobox>
    </>
  );
}

/** One choice of a searchable list: the value sent with the form, the words shown and, if wanted, a small icon shown before them. */
export type FilterOption = { value: string; label: string; icon?: ReactNode };

/** The open part of a searchable drop-down (inside a `Combobox`): the search box, the "nothing found" words and the list of options that narrows as you type. */
function FilterList({ searchLabel, emptyLabel }: { searchLabel: string; emptyLabel: string }) {
  return (
    <ComboboxContent>
      <ComboboxInput showTrigger={false} placeholder={searchLabel} aria-label={searchLabel} />
      <ComboboxEmpty>{emptyLabel}</ComboboxEmpty>
      <ComboboxList>{(o: FilterOption) => <ComboboxItem key={o.value} value={o}>{o.icon}{o.label}</ComboboxItem>}</ComboboxList>
    </ComboboxContent>
  );
}

type MultiSelectProps = { name: string; id?: string; options: FilterOption[]; defaultValue?: string[]; onChange?: (value: string[]) => void; placeholder: string; searchLabel: string; emptyLabel: string };

/** A drop-down with a search box to tick several choices: a button shows the ones chosen (or `placeholder`) and opens a list that narrows as you type. The choices are sent in the form under `name`, separated by commas. Build the words once with `SearchableMultiSelect`. */
export function MultiSelect({ name, id, options, defaultValue = [], onChange, placeholder, searchLabel, emptyLabel }: MultiSelectProps) {
  const field = useRef<HTMLInputElement>(null);
  const { value, pick, clear } = usePickedValue(defaultValue.join(","), "");
  useFormReset(field, clear);
  const chosen = options.filter((o) => value.split(",").includes(o.value));
  return (
    <>
      <input ref={field} type="hidden" name={name} value={chosen.map((o) => o.value).join(",")} />
      <Combobox multiple items={options} value={chosen} itemToStringLabel={(o: FilterOption) => o.label} isItemEqualToValue={(a: FilterOption, b: FilterOption) => a.value === b.value} onValueChange={(next: FilterOption[]) => { const values = next.map((o) => o.value); pick(values.join(",")); onChange?.(values); }}>
        <ComboboxTrigger render={<Button id={id} type="button" variant="outline" className="w-full justify-between font-normal" />}><span className="truncate">{chosen.length > 0 ? chosen.map((o) => o.label).join(", ") : placeholder}</span></ComboboxTrigger>
        <FilterList searchLabel={searchLabel} emptyLabel={emptyLabel} />
      </Combobox>
    </>
  );
}

/** For a field that keeps its choice in its own state: the value shown is the one the person picked, else the value the server gave (`saved`), else `fallback`. When the server's value changes (the page was refreshed after a save, or another record took the place of this one) the pick is dropped, so the field always follows what is stored instead of keeping what it was first shown with. `pick` sets a choice, `clear` drops it (the form was reset). */
export function usePickedValue(saved: string | undefined, fallback: string) {
  const [picked, setPicked] = useState<string | null>(null);
  const [seen, setSeen] = useState(saved);
  if (seen !== saved) { setSeen(saved); setPicked(null); }
  return { value: picked ?? saved ?? fallback, pick: (value: string) => setPicked(value), clear: () => setPicked(null) };
}

/** For a field that keeps its choice in its own state: tells its form (a "change" event from `field`, a hidden input already holding the new value) each time `value` changes after the first showing, so a form that saves by itself notices. */
export function useAnnounceChange(field: RefObject<HTMLElement | null>, value: unknown) {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    field.current?.dispatchEvent(new Event("change", { bubbles: true }));
  }, [field, value]);
}

/** Calls `onReset` when the form that holds `field` is reset (such as after a successful save), so a field that keeps its value in its own state can go back to what is stored, like a normal field does. */
export function useFormReset(field: RefObject<HTMLElement | null>, onReset: () => void) {
  useEffect(() => {
    const form = (field.current as HTMLInputElement | null)?.form;
    if (!form) return;
    form.addEventListener("reset", onReset);
    return () => form.removeEventListener("reset", onReset);
  }, [field, onReset]);
}
